// src/components/DependentClassifier/DependentClassifier.js
import React, { useState } from "react";
import "./DependentClassifier.css";
import Header from "../Header/Header";

const DependentClassifier = () => {
  // VIEW MODE: what are we focusing on?
  // "dependent" = independent vs dependent (QC / QR)
  // "hoh"       = Head of Household focus
  const [viewMode, setViewMode] = useState("dependent");

  // BASIC FACTS – start with NOTHING selected
  const [relationship, setRelationship] = useState(null);
  // child, grandchild, sibling, nieceNephew, parent, grandparent, otherRelative, unrelated

  const [ageBracket, setAgeBracket] = useState(null);
  // 0-16, 17-18, 19-23-student, 19-23-nonstudent, 24plus, disabled

  const [livedWithYou, setLivedWithYou] = useState(null);
  // allYear, moreThanHalf, lessThanHalf

  const [childProvidedOwnSupport, setChildProvidedOwnSupport] = useState(null);

  // QR / SUPPORT / INCOME
  const [lowIncome, setLowIncome] = useState(null); // below QR income limit
  const [youProvideHalfSupport, setYouProvideHalfSupport] = useState(null); // >50% of their support

  // HOH home-cost test
  const [youPayHalfHomeCost, setYouPayHalfHomeCost] = useState(null);
  // >50% of cost of keeping up the home (rent, utilities, food eaten in the home, etc.)

  // CTC / EITC / residency extras (context only)
  const [hasSSN, setHasSSN] = useState(false);
  const [isUSPerson, setIsUSPerson] = useState(false);

  // ---------- RELATIONSHIP FLAGS ----------
  const isQCRelationship = ["child", "sibling", "nieceNephew", "grandchild"].includes(
    relationship
  );

  const isListedQRRelative = [
    "child",
    "sibling",
    "nieceNephew",
    "parent",
    "grandparent",
    "otherRelative",
    "grandchild",
  ].includes(relationship);

  const isParent = relationship === "parent";
  const isUnrelated = relationship === "unrelated";

  // ---------- AGE LOGIC ----------
  const isDisabled = ageBracket === "disabled";

  // QC age: <19 OR 19–23 full-time student OR disabled
  const isUnder19 = ageBracket === "0-16" || ageBracket === "17-18";
  const is19to23Student = ageBracket === "19-23-student";

  const ageUnder24StudentRule = isUnder19 || is19to23Student || isDisabled;

  // ---------- RESIDENCY FLAGS ----------
  const livedAllYear = livedWithYou === "allYear";
  const livedMoreThanHalf = livedWithYou === "moreThanHalf" || livedAllYear;

  // ---------- 1) QC FOR DEPENDENCY (SIMPLIFIED LEARNING VERSION) ----------
  const qcRelationshipPass = isQCRelationship;
  const qcAgePass = ageUnder24StudentRule;
  const qcResidencyPass = livedMoreThanHalf;
  const qcSelfSupportPass =
    childProvidedOwnSupport === null ? false : !childProvidedOwnSupport;

  const isQCForDependency =
    qcRelationshipPass && qcAgePass && qcResidencyPass && qcSelfSupportPass;

  // ---------- 2) QR (SIMPLIFIED LEARNING VERSION) ----------
  // QR: not QC, low income, you provide >50% support,
  // and either listed relative OR unrelated who lives with you all year.
  const qrNotQCChildPass = !isQCForDependency;
  const qrIncomePass = lowIncome === true;
  const qrSupportPass = youProvideHalfSupport === true;
  const qrRelationshipOrResidencePass = isListedQRRelative || livedAllYear;

  const isQREligible =
    qrNotQCChildPass &&
    qrIncomePass &&
    qrSupportPass &&
    qrRelationshipOrResidencePass;

  // ---------- 3) HOH QUALIFYING PERSON (SIMPLIFIED LEARNING VIEW) ----------
  // HOH "person" side:
  // - Any QC works
  const hasHOHPersonByQC = isQCForDependency;

  // - OR QR (non-QC) person who meets the HOH rules:
  //   * Parent QR: does NOT need to live with you.
  //   * Non-parent listed relative QR: must live with you ALL YEAR.
  //   * Unrelated QR: NEVER a HOH qualifying person.
  const hasHOHPersonByQR =
    isQREligible &&
    (isParent || (!isParent && isListedQRRelative && livedAllYear));

  const hohPersonExists = hasHOHPersonByQC || hasHOHPersonByQR;
  const hohHomeCostPass = youPayHalfHomeCost === true;

  // ---------- REASONING ARRAYS (rewritten in "conditions" language) ----------

  const qcConditions = [
    {
      rule: "Relationship condition",
      passes: qcRelationshipPass,
      detail: qcRelationshipPass
        ? "In this tool, you chose a child / sibling / niece-nephew / grandchild, which usually fits the Qualifying Child relationship rule."
        : "In this tool, the relationship you chose is outside the typical QC list (child, stepchild, foster child, sibling, niece/nephew, grandchild).",
    },
    {
      rule: "Age / student / disability condition",
      passes: qcAgePass,
      detail: qcAgePass
        ? "You chose an age bucket that fits the simplified QC rule: under 19, or 19–23 full-time student, or any age if permanently disabled."
        : "You chose an age bucket that is outside the simplified QC rule (not under 19, not a 19–23 full-time student, and not marked as disabled).",
    },
    {
      rule: "Residency condition",
      passes: qcResidencyPass,
      detail: qcResidencyPass
        ? "You indicated they lived with you more than half the year (time away at college generally counts as living with you for this rule)."
        : "You indicated they did not live with you more than half the year, so the usual QC residency condition would not be met in this simplified model.",
    },
    {
      rule: "Self-support condition",
      passes: qcSelfSupportPass,
      detail: qcSelfSupportPass
        ? "You said they did NOT pay more than half of their own support, which matches the self-support part of the QC rule."
        : "You said they paid more than half of their own support, or you have not answered this yet, which would cause the self-support condition to fail under QC rules in this tool.",
    },
  ];

  const qrConditions = [
    {
      rule: "Not a Qualifying Child (QC) under this tool",
      passes: qrNotQCChildPass,
      detail: qrNotQCChildPass
        ? "Based on the QC conditions above, they did not meet all the QC checks in this tool, so the Qualifying Relative (QR) path is what many people would review next."
        : "Under the simplified QC checks above, they already meet all QC conditions in this tool, so typically you would not use the QR rules for them.",
    },
    {
      rule: "Income below QR gross income limit",
      passes: qrIncomePass,
      detail: qrIncomePass
        ? "You said their gross income is below the simplified QR limit (roughly around the yearly IRS threshold for QR)."
        : "You said their gross income is above the QR limit, or you have not answered this yet, so the QR income condition would not be met in this tool.",
    },
    {
      rule: "You provide more than half of their total support",
      passes: qrSupportPass,
      detail: qrSupportPass
        ? "You indicated that you pay more than half of their total support, which fits the support test for QR."
        : "You indicated that you do NOT provide more than half of their total support, or you have not answered this yet, so the QR support condition would not be met in this tool.",
    },
    {
      rule: "Relationship OR full-year residence condition",
      passes: qrRelationshipOrResidencePass,
      detail:
        isListedQRRelative && !livedAllYear
          ? "They are on the IRS-style allowed relative list in this tool (e.g., parent, grandparent, sibling, niece/nephew, certain in-laws), so they do not have to live with you all year if the other QR tests are met."
          : !isListedQRRelative && livedAllYear
          ? "They are not on the allowed relative list in this tool, but you said they lived with you ALL YEAR. That can satisfy the 'member of household all year' path for QR."
          : isListedQRRelative && livedAllYear
          ? "They are both an allowed relative and lived with you all year, which fits either path for this QR relationship/residency condition."
          : "They are not on the allowed relative list in this tool and also did not live with you all year, or you have not answered yet, so this QR relationship/residency condition would not be met here.",
    },
  ];

  // HOH conditions are about having a qualifying person AND paying >50% of keeping up the home.
  let hohConditions = [];

  hohConditions.push({
    rule: "Having a possible HOH 'qualifying person' through QC",
    passes: hasHOHPersonByQC,
    detail: hasHOHPersonByQC
      ? "Under these simplified rules, the person fits the QC pattern, and a Qualifying Child can often be a 'qualifying person' for Head of Household."
      : "Under these simplified QC checks, they do not meet all QC conditions, so HOH would only be possible (if at all) through certain Qualifying Relative (QR) paths.",
  });

  if (!hasHOHPersonByQC) {
    hohConditions.push(
      {
        rule: "Having a possible HOH 'qualifying person' through QR",
        passes: isQREligible,
        detail: isQREligible
          ? "In this tool, they meet the simplified QR dependency pattern. For some QR relationships, they may be able to count as a 'qualifying person' for HOH."
          : "They do not meet this tool’s simplified QR dependency pattern, so even the QR path would not usually provide a HOH qualifying person.",
      },
      {
        rule: "QR – residency vs. parent exception",
        passes: hasHOHPersonByQR,
        detail: !isQREligible
          ? "Because they did not pass the QR pattern in this tool, the extra HOH residency/parent conditions do not come into play here."
          : isParent
          ? "In this tool, you marked them as a parent who is also a QR dependent. For HOH, a parent does not have to live with you if other tests are met."
          : isUnrelated
          ? "You marked them as unrelated. Under HOH rules, an unrelated dependent is not treated as a 'qualifying person' for HOH."
          : livedAllYear
          ? "You marked them as a listed relative (not a parent) who lived with you ALL YEAR. Under HOH rules, this pattern can fit the HOH residency condition for a qualifying person."
          : "You marked them as a listed relative (not a parent) who did not live with you ALL YEAR, or have not answered yet. That usually fails the HOH residency requirement for a qualifying person.",
      }
    );
  }

  hohConditions.push({
    rule: "You pay more than half of the cost of keeping up the home",
    passes: hohHomeCostPass,
    detail: hohHomeCostPass
      ? "You indicated you pay more than half of the total yearly cost of keeping up the home (rent/mortgage, utilities, property tax, home insurance, repairs, food eaten in the home). This is a key piece of the HOH test."
      : "You did not indicate that you pay more than half of the yearly cost of keeping up the home, or you have not answered yet. Even with a qualifying person, HOH generally requires paying >50% of these home costs.",
  });

  const countPasses = (conditions) => conditions.filter((c) => c.passes).length;

  const qcPassCount = countPasses(qcConditions);
  const qrPassCount = countPasses(qrConditions);
  const hohPassCount = countPasses(hohConditions);

  // Only show the big “conditions” section after at least one core answer
  const anyCoreAnswerChosen =
    relationship !== null ||
    ageBracket !== null ||
    livedWithYou !== null ||
    childProvidedOwnSupport !== null ||
    lowIncome !== null ||
    youProvideHalfSupport !== null ||
    youPayHalfHomeCost !== null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-100 via-orange-50 to-slate-50 text-slate-900">
      <Header />
      <div className="dc-container">
        <h2 className="dc-title">Dependent Rules Explorer (Educational)</h2>

        <p className="dc-disclaimer">
          This is a <strong>teaching tool</strong>. It lets you see how common IRS-style
          conditions for dependents and Head of Household usually work, based on answers
          you choose here. It does <strong>not</strong> file a tax return, does <strong>not</strong> tell you
          what to claim, and does <strong>not</strong> replace IRS.gov or a tax professional.
        </p>

        {/* VIEW MODE TOGGLE */}
        <div className="dc-mode-toggle">
          <button
            className={
              "dc-pill" +
              (viewMode === "dependent" ? " dc-pill--active" : "")
            }
            onClick={() => setViewMode("dependent")}
          >
            Independent vs. Dependent
          </button>
          <button
            className={
              "dc-pill" + (viewMode === "hoh" ? " dc-pill--active" : "")
            }
            onClick={() => setViewMode("hoh")}
          >
            Head of Household (HOH)
          </button>
        </div>
        <p className="dc-note">
          {viewMode === "dependent"
            ? "Use this view to see how the Qualifying Child and Qualifying Relative conditions build the idea of 'dependent' versus 'independent'."
            : "Use this view to see how a possible dependent connects to Head of Household rules and the cost of keeping up a home."}
        </p>

        {/* INPUT GRID */}
        <div className="dc-grid">
          {/* Relationship */}
          <div className="dc-card">
            <h3>Relationship to You</h3>
            <div className="dc-options">
              {[
                { value: "child", label: "Child / Stepchild / Foster" },
                { value: "grandchild", label: "Grandchild" },
                { value: "sibling", label: "Sibling / Step-sibling" },
                { value: "nieceNephew", label: "Niece / Nephew" },
                { value: "parent", label: "Parent" },
                { value: "grandparent", label: "Grandparent" },
                { value: "otherRelative", label: "Other Relative (aunt/uncle, in-law)" },
                { value: "unrelated", label: "Unrelated (friend / roommate)" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  className={
                    "dc-pill" +
                    (relationship === opt.value ? " dc-pill--active" : "")
                  }
                  onClick={() => setRelationship(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Age */}
          <div className="dc-card">
            <h3>Age / Student Status</h3>
            <div className="dc-options">
              {[
                { value: "0-16", label: "0–16 (not disabled)" },
                { value: "17-18", label: "17–18 (not disabled)" },
                { value: "19-23-student", label: "19–23 full-time student" },
                { value: "19-23-nonstudent", label: "19–23 not a full-time student" },
                { value: "24plus", label: "24+ (not disabled)" },
                { value: "disabled", label: "Any age – permanently disabled" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  className={
                    "dc-pill" +
                    (ageBracket === opt.value ? " dc-pill--active" : "")
                  }
                  onClick={() => setAgeBracket(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Residency */}
          <div className="dc-card">
            <h3>Did they live with you…</h3>
            <div className="dc-options">
              {[
                { value: "allYear", label: "All year (or away at college)" },
                { value: "moreThanHalf", label: "More than half the year" },
                { value: "lessThanHalf", label: "Half or less" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  className={
                    "dc-pill" +
                    (livedWithYou === opt.value ? " dc-pill--active" : "")
                  }
                  onClick={() => setLivedWithYou(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Self-support */}
          <div className="dc-card">
            <h3>Did they pay &gt;50% of their own support?</h3>
            <div className="dc-options">
              {["No", "Yes"].map((label) => {
                const val = label === "Yes";
                return (
                  <button
                    key={label}
                    className={
                      "dc-pill" +
                      (childProvidedOwnSupport === val ? " dc-pill--active" : "")
                    }
                    onClick={() => setChildProvidedOwnSupport(val)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* QR income */}
          <div className="dc-card">
            <h3>Is their gross income below the QR limit?</h3>
            <p className="dc-note">
              (This uses a simplified idea of the yearly QR gross income threshold.)
            </p>
            <div className="dc-options">
              {["Yes", "No"].map((label) => {
                const val = label === "Yes";
                return (
                  <button
                    key={label}
                    className={
                      "dc-pill" + (lowIncome === val ? " dc-pill--active" : "")
                    }
                    onClick={() => setLowIncome(val)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Support you provide (for QR) */}
          <div className="dc-card">
            <h3>Do YOU provide &gt;50% of their total support?</h3>
            <div className="dc-options">
              {["Yes", "No"].map((label) => {
                const val = label === "Yes";
                return (
                  <button
                    key={label}
                    className={
                      "dc-pill" +
                      (youProvideHalfSupport === val ? " dc-pill--active" : "")
                    }
                    onClick={() => setYouProvideHalfSupport(val)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* HOH home cost test – only show in HOH mode */}
          {viewMode === "hoh" && (
            <div className="dc-card">
              <h3>Do YOU pay &gt;50% of the cost of keeping up the home?</h3>
              <p className="dc-note">
                For HOH, this means more than half of the total yearly cost of rent or
                mortgage, utilities, property taxes, home insurance, repairs, and food eaten
                in the home.
              </p>
              <div className="dc-options">
                {["Yes", "No"].map((label) => {
                  const val = label === "Yes";
                  return (
                    <button
                      key={label}
                      className={
                        "dc-pill" +
                        (youPayHalfHomeCost === val ? " dc-pill--active" : "")
                      }
                      onClick={() => setYouPayHalfHomeCost(val)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

      
        </div>

        {/* RESULTS + CONDITIONS */}
        <div className="dc-results">
          <h3>How the Conditions Work (Educational Only)</h3>

          {!anyCoreAnswerChosen && (
            <p className="dc-summary">
              Start by choosing a relationship, age, or living situation above. As you
              answer, this section will light up which conditions are marked as met in this
              tool.
            </p>
          )}

          {anyCoreAnswerChosen && (
            <>
              {viewMode === "dependent" && (
                <>
                  {/* QC block */}
                  <div className="dc-reason-block">
                    <h4>
                      To be a{" "}
                      <span className="dc-highlight">Qualifying Child (QC)</span> under
                      this simplified view:
                    </h4>
                    <p className="dc-summary">
                      A typical QC pattern needs <strong>all of these conditions</strong>.
                      Below you can see how many of them you indicated as met in this tool.
                    </p>
                    <p className="dc-summary">
                      Conditions met in this tool: <strong>{qcPassCount}</strong> out of{" "}
                      <strong>{qcConditions.length}</strong>.
                    </p>
                    <ul>
                      {qcConditions.map((c, i) => (
                        <li
                          key={i}
                          className={c.passes ? "dc-rule-pass" : "dc-rule-fail"}
                        >
                          <strong>
                            {c.passes
                              ? "✔ Condition marked as met – "
                              : "✘ Condition not met – "}
                            {c.rule}:
                          </strong>{" "}
                          {c.detail}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* QR block */}
                  <div className="dc-reason-block">
                    <h4>
                      To be a{" "}
                      <span className="dc-highlight">Qualifying Relative (QR)</span> under
                      this simplified view:
                    </h4>
                    <p className="dc-summary">
                      QR patterns have a different set of conditions. Again, the idea here
                      is to see how the QR rule is built, not to tell you what to put on a
                      tax return.
                    </p>
                    <p className="dc-summary">
                      Conditions met in this tool: <strong>{qrPassCount}</strong> out of{" "}
                      <strong>{qrConditions.length}</strong>.
                    </p>
                    <ul>
                      {qrConditions.map((c, i) => (
                        <li
                          key={i}
                          className={c.passes ? "dc-rule-pass" : "dc-rule-fail"}
                        >
                          <strong>
                            {c.passes
                              ? "✔ Condition marked as met – "
                              : "✘ Condition not met – "}
                            {c.rule}:
                          </strong>{" "}
                          {c.detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}

              {viewMode === "hoh" && (
                <div className="dc-reason-block">
                  <h4>
                    To have a possible{" "}
                    <span className="dc-highlight">Head of Household (HOH)</span>{" "}
                    situation:
                  </h4>
                  <p className="dc-summary">
                    HOH usually connects two ideas: having a person who could count as a
                    &quot;qualifying person&quot; and paying more than half of the cost of
                    keeping up the home. This tool only shows the{" "}
                    <strong>structure</strong> of those ideas.
                  </p>
                  <p className="dc-summary">
                    Conditions met in this tool: <strong>{hohPassCount}</strong> out of{" "}
                    <strong>{hohConditions.length}</strong>.
                  </p>
                  <ul>
                    {hohConditions.map((c, i) => (
                      <li
                        key={i}
                        className={c.passes ? "dc-rule-pass" : "dc-rule-fail"}
                      >
                        <strong>
                          {c.passes
                            ? "✔ Condition marked as met – "
                            : "✘ Condition not met – "}
                          {c.rule}:
                        </strong>{" "}
                        {c.detail}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          <p className="dc-disclaimer dc-disclaimer-bottom">
            This screen is a <strong>map of conditions</strong>, not a verdict. Even if
            every condition looks &quot;met&quot; in this tool, only your full factual
            situation and the official IRS rules determine whether someone is your
            dependent or whether you can file as Head of Household. Use this to understand
            the questions, then confirm with:
            <br />
            • IRS.gov (publications, interactive tools) <br />
            • or a qualified tax professional
          </p>
        </div>
      </div>
    </div>
  );
};

export default DependentClassifier;
